import { Component, Input, OnInit, AfterViewInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRippleModule } from '@angular/material/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DialogService } from '@adgate/common/services/dialog-service';
import { ObjectDescPipe } from '@adgate/common/pipes/object-desc-pipe';
import { ProductionRequest } from '@adgate/modules/production/types/production-request.type';
import { ProductionService } from '@adgate/modules/production/services/production-service';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { AuthenticationService } from '@adgate/common/services/authentication-service';
import { StateService } from '@adgate/common/services/state-service';
import { Privilege } from '@adgate/modules/admin/types/privilege.type';
import { ProductionVersionFilter } from '@adgate/modules/production/types/production-version-filter.type';
import { Status } from '@adgate/common/types/status.type';
import { ProductionVersion } from '@adgate/modules/production/types/production-version.type';
import { ProductionVersionEditDialogComponent } from '@adgate/modules/production/dialogs/production-version-edit-dialog-component/production-version-edit-dialog-component';
import { filter, switchMap } from 'rxjs';

@Component({
  selector: 'production-request-version-grid-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatTooltip,
    ClipboardModule,
    MatRippleModule,
    MatSnackBarModule,
    ObjectDescPipe
  ],
  providers: [DatePipe],
  templateUrl: './production-request-version-grid-component.html',
  styleUrl: './production-request-version-grid-component.scss',
})
export class ProductionRequestVersionGridComponent implements OnInit, AfterViewInit
{
  private dialogService: DialogService = inject(DialogService);
  private productionService: ProductionService = inject(ProductionService);
  private authService: AuthenticationService = inject(AuthenticationService);
  private stateService: StateService = inject(StateService);
  private _snackBar: MatSnackBar = inject(MatSnackBar);
  private cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

  @Input() parentRequest!: ProductionRequest;

  @ViewChild(MatSort) sort!: MatSort;

  displayedColumns: string[] = ['type', 'name', 'notes', 'created', 'txId', 'status', 'actions'];
  dataSource: MatTableDataSource<any>;

  showTable: boolean = false;
  allowUpdateRequest: boolean = false;
  allowUpdateVersion: boolean = false;
  filter: ProductionVersionFilter = new ProductionVersionFilter();

  constructor()
  {
    this.dataSource = new MatTableDataSource<any>([]);
  }

  ngOnInit(): void
  {
    this.initPrivileges();
    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
  }

  onRefresh()
  {
    if( this.parentRequest != null ) {
      this.dialogService.showProgress();
      this.filter.requestId = this.parentRequest.id;
      this.productionService.getProductionVersions(this.filter).subscribe({
        next: (r: RESTApplicationResponse) => {
          this.dataSource.data = r.data;
          this.showTable = this.dataSource.data.length > 0;
          this.dialogService.hideProgress();
          this.cdr.detectChanges();
        },
        error: (err: Error) => {
          this.dialogService.openErrorDialog(err.message);
        }
      });
    }
  }

  onViewHistory(version: any): void
  {
    // TODO: Implement view history
  }

  onCopyTxId(txId: string)
  {
    this._snackBar.open(`${txId} copied to clipboard`, '', { duration: 2000 });
  }

  allowDeleteVersion(): boolean
  {
    return this.allowUpdateRequest && (this.parentRequest.status.id == Status.PRODUCTION_REQUEST_CREATED);
  }

  onDeleteVersion(v: ProductionVersion): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete version "${v.name}"?`,
      {
        title: 'Delete Production Version',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        return this.productionService.deleteProductionVersion(v.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onEditVersion(version: ProductionVersion): void
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(ProductionVersionEditDialogComponent, {
      data: version,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: ProductionVersion | null) => (result !== null) ),
      switchMap((result:ProductionVersion) => {
        this.dialogService.showProgress();
        return this.productionService.updateProductionVersion(result);
      }),
      switchMap((r:RESTApplicationResponse) => {
        successMessage = r.message;
        return this.productionService.getProductionVersions(this.filter);
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => { 
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch (property) {
        case 'type': return item.type?.description;
        case 'name': return item.name;
        case 'notes': return item.notes;
        case 'created': return item.dateCreated;
        case 'txId': return item.txId;
        default: return item[property];
      }
    };
  }

  private initPrivileges(): void
  {
    let decodedToken = this.authService.decodeToken(this.stateService.data.userToken);
    this.allowUpdateRequest = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.PROD_UPDATEORDER]);
    this.allowUpdateVersion = this.authService.checkPrivilegeOnToken(decodedToken, [Privilege.PROD_UPDATEVERSION]);
  }
}
